import { Color } from '@persona-id/design-tokens';
import { ceil, isInteger } from 'lodash';
import { ComponentProps, ReactElement } from 'react';
import { AxisInterval } from 'recharts/types/util/types';

import BarAndLineChartWithLegendV2 from 'dashboard/components/BarAndLineChartV2';
import {
  formatChartDateHour,
  formatChartDateLong,
  formatChartDateLongWithDay,
  formatChartDateMonth,
} from 'dashboard/lib/utils/formatters';
import { ReportsChartDatum, ReportsOverTimeResponse } from 'dashboard/models/metrics-reports';

const LINE_COLORS = [Color.CorePurple800, Color.CoreTeal900];
const BAR_COLORS = [Color.CoreTeal100, Color.CorePurple200];

// Ordering matters here. This is the order in which the bars are shown.
export interface ReportsComposedChartProps extends Pick<ReportsOverTimeResponse, 'period'> {
  data: ReportsChartDatum[];
  barKeys: Partial<Record<keyof ReportsChartDatum, string>>;
  lineKeys: Partial<Record<keyof ReportsChartDatum, string>>;
  xFormatter: ComponentProps<typeof BarAndLineChartWithLegendV2>['xFormatter'];
  barsLabel?: string;
  linesLabel?: string;
  legendIcons: Record<string, ReactElement>;
  interval: AxisInterval;
}

const PERCENTAGE_UPPER_LIMIT = 1;

const ReportsComposedChart = ({
  data,
  period,
  barKeys,
  lineKeys,
  legendIcons,
  interval,
  xFormatter,
}: ReportsComposedChartProps) => {
  const createTooltipHeader = (label: number) => {
    return period === 'week'
      ? `Week of ${formatChartDateLong(label)}`
      : period === 'month'
        ? formatChartDateMonth(label)
        : period === 'day'
          ? formatChartDateLongWithDay(label)
          : formatChartDateHour(label);
  };

  const percentageMax = Math.max(
    ...data.map(function (datum) {
      const percentages = [datum.first_match_rate, datum.subsequent_match_rate].filter(
        Number.isFinite,
      );
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      return Math.max(...percentages);
    }),
  );

  // Gathering the minimum of the charted line key values
  const percentageMin = Math.min(
    ...data.map(function (datum) {
      return Math.min(
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        ...Object.keys(lineKeys)
          .map(function (key) {
            return datum[key as keyof ReportsChartDatum];
          })
          .filter(Number.isFinite)
          .flat(),
      );
    }),
  );

  const formatReportsNumberLarge = (value: number): string => {
    const absValue = Math.abs(value);
    const sign = Math.sign(value);
    const rawValueK = (absValue / 10 ** 3) * sign;
    const rawValueM = (absValue / 10 ** 6) * sign;

    if (absValue < 10 ** 3) {
      return `${absValue * sign}`;
    } else if (absValue < 10 ** 4) {
      return rawValueK % 1 === 0 ? `${rawValueK}K` : `${rawValueK.toPrecision(2)}K`;
    } else if (absValue < 10 ** 5) {
      return rawValueK % 1 === 0 ? `${rawValueK}K` : `${rawValueK.toPrecision(3)}K`;
    } else if (absValue < 10 ** 6) {
      return `${Math.round(absValue / 10 ** 3) * sign}K`;
    } else if (absValue < 10 ** 7) {
      return rawValueM % 1 === 0 ? `${rawValueM}M` : `${rawValueM.toPrecision(2)}M`;
    } else if (absValue < 10 ** 8) {
      return rawValueM % 1 === 0 ? `${rawValueM}M` : `${rawValueM.toPrecision(3)}M`;
    } else if (absValue < 10 ** 9) {
      return `${Math.round(absValue / 10 ** 6) * sign}M`;
    } else if (absValue < 10 ** 12) {
      return `${Math.round(absValue / 10 ** 9) * sign}B`;
    } else {
      return `${Math.round(absValue / 10 ** 12) * sign}T`;
    }
  };

  // Create a rounded value. When not an integer, we find the precision and then round up
  // from there. We'll want to round up the nearest integer value of the precision - 1
  let maxPrecision = 0;
  if (!isInteger(percentageMax)) {
    // Convert into a string as to avoid any funkiness with scientific notation
    const stringFloat: string = percentageMax.toFixed(10);
    // Discover where the decimal point + decimals are
    const decimalIndex = stringFloat.indexOf('.');
    // Round up to one above the precision. IE : 0.0024 -> 0.0030
    maxPrecision = Math.max(0, Math.min(4, stringFloat.length - decimalIndex - 1) - 1);
  }
  const roundedMax = ceil(percentageMax * 10 ** maxPrecision) / 10 ** maxPrecision;

  return (
    <BarAndLineChartWithLegendV2
      data={data}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      legendFormatter={(value: any) => {
        return (
          barKeys[value as keyof ReportsChartDatum] ?? lineKeys[value as keyof ReportsChartDatum]
        );
      }}
      barKeys={Object.keys(barKeys)}
      barsLabel='Volume'
      lineKeys={Object.keys(lineKeys)}
      linesLabel='Rate'
      colors={LINE_COLORS}
      stackColors={BAR_COLORS}
      xDataKey='created_timestamp'
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      createTooltipHeader={createTooltipHeader}
      xFormatter={xFormatter}
      interval={interval}
      // Always going to stack these
      stackedBars={true}
      initialFilteredLegendKeys={[]}
      // By Default, show the Observations with 0
      showZeros={true}
      // Dont scroll (no weird space on the right)
      scrollEnabled={false}
      legendIcons={legendIcons}
      // High number of digits for the match rate
      legendNumberOpts={{ maximumFractionDigits: 4 }}
      // The domain of the yAxis. When it's above 4, we'll not need to actually limit anything
      percentageAxisDomain={
        // When its above the upper limit (or is 0), we'll let it auto set the axis domain.
        // In the event that we have removed data at all in the window of observation, default to an
        // auto declaration of the axes, that'll prevent any errors.
        percentageMax >= PERCENTAGE_UPPER_LIMIT || percentageMax === 0 || isNaN(percentageMin)
          ? // If its 100, we'll need to ensure its not [100,100], setting it in the middle of the axis
            percentageMin > 96
            ? // Thus, we'll simply need to take [96,100] or [min, 100]
              // we use 96 since, by default, recharts will have 5 ticks shown from the min
              // on the lower end 0,1,2,3,4 on the higher end 96, 97, 98, 99, 100
              [96, 100]
            : ['auto', 'auto']
          : [0, roundedMax === 0 ? 4 : roundedMax]
      }
      // Dont let the reports info get cut off by the screen
      allowEscapeViewBoxX={false}
      numbersAxisFormatter={formatReportsNumberLarge}
      xAxisLabel='Run completion date'
    />
  );
};

export default ReportsComposedChart;
